import React, { useState, useEffect, useMemo } from 'react';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

export default function ExpertRulesMasterPage() {
  const [activeTab, setActiveTab] = useState('rules'); // 'rules' | 'mechanisms' | 'comments'
  const [rules, setRules] = useState([]);
  const [mechanisms, setMechanisms] = useState([]);
  const [comments, setComments] = useState([]);
  
  // Master references for visual condition builder
  const [groups, setGroups] = useState([]);
  const [organisms, setOrganisms] = useState([]);
  const [antimicrobials, setAntimicrobials] = useState([]);
  const [specimens, setSpecimens] = useState([]);
  const [standards, setStandards] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  // Search & Filter state
  const [ruleSearch, setRuleSearch] = useState('');
  const [ruleStatusFilter, setRuleStatusFilter] = useState('ALL');
  const [ruleActionFilter, setRuleActionFilter] = useState('ALL');

  const [mechSearch, setMechSearch] = useState('');
  const [mechStatusFilter, setMechStatusFilter] = useState('ALL');
  const [mechAlertFilter, setMechAlertFilter] = useState('ALL');

  const [commentSearch, setCommentSearch] = useState('');
  const [commentCategoryFilter, setCommentCategoryFilter] = useState('ALL');
  const [commentStatusFilter, setCommentStatusFilter] = useState('ALL');

  // Modals
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isMechModalOpen, setIsMechModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [viewDetailModal, setViewDetailModal] = useState({ isOpen: false, type: null, data: null });
  const [historyModal, setHistoryModal] = useState({ isOpen: false, type: null, data: null });

  // Rule Form State
  const [ruleForm, setRuleForm] = useState({
    ruleID: null,
    ruleCode: '',
    ruleName: '',
    description: '',
    status: 'Draft',
    isActive: true,
    priority: 10,
    organismGroupID: '',
    organismID: '',
    antimicrobialID: '',
    specimenID: '',
    standardID: '',
    // Visual Condition Builder parts
    conditionType: 'ORGANISM_DRUG_RESISTANCE',
    targetDrugCode: '',
    targetOrganismCode: '',
    expectedInterpretation: 'R', // R, S, I
    zoneThresholdOperator: '<=',
    zoneThresholdVal: '',
    customConditionJSON: '',
    showRawConditionJSON: false,
    // Action Builder parts
    actionType: 'OVERRIDE', // OVERRIDE, CRITICAL, SUPPRESS, COMMENT, WARN
    overrideInterpretation: 'R',
    alertSeverity: 'HIGH',
    alertTitle: 'Critical Resistance Phenotype Alert',
    alertMessage: 'Confirm identification and repeat susceptibility testing.',
    suppressDrugCodes: '',
    commentTemplateText: 'Isolate exhibits intrinsic/atypical resistance pattern. Clinical correlation advised.',
    showRawActionJSON: false
  });
  const [ruleErrors, setRuleErrors] = useState({});

  // Mechanism Form State
  const [mechForm, setMechForm] = useState({
    mechanismID: null,
    code: '',
    name: '',
    category: 'Enzymatic Resistance',
    organismScope: 'Enterobacterales, Pseudomonas spp.',
    associatedPhenotype: 'ESBL / Carbapenem Resistance',
    infectionControlAlert: true,
    alertType: 'Mandatory Isolation Alert',
    clinicalSignificance: 'High epidemiological and infection control priority.',
    status: 'Active',
    isActive: true
  });
  const [mechErrors, setMechErrors] = useState({});

  // Comment Form State
  const [commentForm, setCommentForm] = useState({
    commentID: null,
    category: 'AST',
    title: '',
    commentText: '',
    usageScope: 'All Routine Specimen Isolates',
    status: 'Active',
    isActive: true
  });
  const [commentErrors, setCommentErrors] = useState({});

  // Notification Toast
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: 'success', message: '' });
    }, 4500);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [rRes, mRes, cRes, grpRes, orgRes, drugRes, spRes, stRes] = await Promise.all([
        microbiologyService.getExpertRules(),
        microbiologyService.getResistanceMechanisms(),
        microbiologyService.getCommentTemplates(),
        microbiologyService.getOrganismGroups(),
        microbiologyService.getOrganisms(),
        microbiologyService.getAntimicrobials(),
        microbiologyService.getSpecimens(),
        microbiologyService.getStandards()
      ]);

      setRules(rRes?.data || []);
      setMechanisms(mRes?.data || []);
      setComments(cRes?.data || []);
      setGroups(grpRes?.data || []);
      setOrganisms(orgRes?.data || []);
      setAntimicrobials(drugRes?.data || []);
      setSpecimens(spRes?.data || []);
      setStandards(stRes?.data || []);
    } catch (err) {
      console.error('Error loading expert studio data:', err);
      showToast('Failed to load expert studio records.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // -------------------------------------------------------------
  // Tab 1: Expert Rules Handlers
  // -------------------------------------------------------------
  const handleOpenCreateRule = () => {
    setRuleForm({
      ruleID: null,
      ruleCode: `RULE_${Date.now().toString().slice(-4)}`,
      ruleName: '',
      description: '',
      status: 'Draft',
      isActive: true,
      priority: (rules.length + 1) * 10,
      organismGroupID: groups[0]?.organismGroupID || '',
      organismID: '',
      antimicrobialID: antimicrobials[0]?.antimicrobialID || '',
      specimenID: '',
      standardID: standards[0]?.standardID || '',
      conditionType: 'ORGANISM_DRUG_RESISTANCE',
      targetDrugCode: antimicrobials[0]?.drugCode || 'FOX',
      targetOrganismCode: '',
      expectedInterpretation: 'R',
      zoneThresholdOperator: '<=',
      zoneThresholdVal: '21',
      customConditionJSON: '',
      showRawConditionJSON: false,
      actionType: 'OVERRIDE',
      overrideInterpretation: 'R',
      alertSeverity: 'HIGH',
      alertTitle: 'Atypical / Critical Phenotype Alert',
      alertMessage: 'Phenotype discordant with clinical standard. Confirm purity and repeat testing.',
      suppressDrugCodes: '',
      commentTemplateText: 'Isolate exhibits intrinsic resistance. Treatment with this agent is not advised.',
      showRawActionJSON: false
    });
    setRuleErrors({});
    setIsRuleModalOpen(true);
  };

  const handleEditRule = (rule) => {
    let parsedCond = {};
    let parsedAct = {};
    try {
      parsedCond = JSON.parse(rule.conditionJSON || '{}');
    } catch (e) {
      parsedCond = {};
    }
    try {
      parsedAct = JSON.parse(rule.actionPayloadJSON || '{}');
    } catch (e) {
      parsedAct = {};
    }

    setRuleForm({
      ruleID: rule.ruleID,
      ruleCode: rule.ruleCode || '',
      ruleName: rule.ruleName || '',
      description: rule.description || '',
      status: rule.isActive ? 'Active' : 'Inactive',
      isActive: rule.isActive !== false,
      priority: rule.priority || 10,
      organismGroupID: rule.organismGroupID || '',
      organismID: rule.organismID || '',
      antimicrobialID: rule.antimicrobialID || '',
      specimenID: rule.specimenID || '',
      standardID: rule.standardID || '',
      conditionType: parsedCond.type || 'ORGANISM_DRUG_RESISTANCE',
      targetDrugCode: parsedCond.drugCode || '',
      targetOrganismCode: parsedCond.organismCode || '',
      expectedInterpretation: parsedCond.interpretation || 'R',
      zoneThresholdOperator: parsedCond.operator || '<=',
      zoneThresholdVal: parsedCond.zone || '',
      customConditionJSON: rule.conditionJSON || '{}',
      showRawConditionJSON: false,
      actionType: rule.actionType || 'OVERRIDE',
      overrideInterpretation: parsedAct.override || 'R',
      alertSeverity: parsedAct.severity || 'HIGH',
      alertTitle: parsedAct.title || 'Critical Alert',
      alertMessage: parsedAct.message || '',
      suppressDrugCodes: Array.isArray(parsedAct.suppressDrugs) ? parsedAct.suppressDrugs.join(', ') : '',
      commentTemplateText: parsedAct.comment || '',
      showRawActionJSON: false
    });
    setRuleErrors({});
    setIsRuleModalOpen(true);
  };

  const handleDuplicateRule = (rule) => {
    handleEditRule({
      ...rule,
      ruleID: null,
      ruleCode: `${rule.ruleCode}_COPY`.slice(0, 30),
      ruleName: `${rule.ruleName} (Copy)`,
      isActive: false
    });
    showToast(`Duplicated rule "${rule.ruleName}". Ready to edit and save.`, 'success');
  };

  const buildConditionJSON = () => {
    if (ruleForm.showRawConditionJSON && ruleForm.customConditionJSON) {
      return ruleForm.customConditionJSON;
    }
    const cond = {
      drugCode: ruleForm.targetDrugCode || (antimicrobials.find(a => a.antimicrobialID === parseInt(ruleForm.antimicrobialID))?.drugCode) || 'ALL',
      interpretation: ruleForm.expectedInterpretation
    };
    if (ruleForm.organismGroupID) cond.organismGroupID = parseInt(ruleForm.organismGroupID);
    if (ruleForm.organismID) cond.organismID = parseInt(ruleForm.organismID);
    if (ruleForm.zoneThresholdVal) {
      cond.zoneThreshold = `${ruleForm.zoneThresholdOperator} ${ruleForm.zoneThresholdVal}mm`;
    }
    return JSON.stringify(cond, null, 2);
  };

  const buildActionPayloadJSON = () => {
    if (ruleForm.showRawActionJSON && ruleForm.customActionJSON) {
      return ruleForm.customActionJSON;
    }
    const act = { action: ruleForm.actionType };
    if (ruleForm.actionType === 'OVERRIDE') {
      act.override = ruleForm.overrideInterpretation;
      act.reason = 'Expert rule automatic interpretation override';
    } else if (ruleForm.actionType === 'CRITICAL') {
      act.severity = ruleForm.alertSeverity;
      act.title = ruleForm.alertTitle;
      act.message = ruleForm.alertMessage;
    } else if (ruleForm.actionType === 'SUPPRESS') {
      act.suppressDrugs = ruleForm.suppressDrugCodes.split(',').map(s => s.trim()).filter(Boolean);
    } else if (ruleForm.actionType === 'COMMENT') {
      act.comment = ruleForm.commentTemplateText;
    } else {
      act.warning = ruleForm.alertMessage || 'Atypical susceptibility pattern detected.';
    }
    return JSON.stringify(act, null, 2);
  };

  const handleSaveRule = async (asActive = false) => {
    const errors = {};
    if (!ruleForm.ruleCode.trim()) errors.ruleCode = 'Rule Code is required';
    if (!ruleForm.ruleName.trim()) errors.ruleName = 'Rule Name is required';
    if (!ruleForm.actionType) errors.actionType = 'Action Type is required';

    if (Object.keys(errors).length > 0) {
      setRuleErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      const conditionJSON = buildConditionJSON();
      const actionPayloadJSON = buildActionPayloadJSON();

      const payload = {
        ruleID: ruleForm.ruleID,
        ruleCode: ruleForm.ruleCode.trim().toUpperCase(),
        ruleName: ruleForm.ruleName.trim(),
        standardID: ruleForm.standardID ? parseInt(ruleForm.standardID) : null,
        organismGroupID: ruleForm.organismGroupID ? parseInt(ruleForm.organismGroupID) : null,
        organismID: ruleForm.organismID ? parseInt(ruleForm.organismID) : null,
        conditionJSON,
        actionType: ruleForm.actionType,
        actionPayloadJSON,
        priority: parseInt(ruleForm.priority) || 10,
        isActive: asActive ? true : (ruleForm.status === 'Active' || ruleForm.status === 'Approved')
      };

      await microbiologyService.saveExpertRule(payload);
      setIsRuleModalOpen(false);
      showToast(`Expert Rule "${payload.ruleName}" saved successfully!`, 'success');
      loadData();
    } catch (err) {
      console.error('Error saving expert rule:', err);
      showToast(err.response?.data?.message || 'Error saving expert rule.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Tab 2: Resistance Mechanisms Handlers
  // -------------------------------------------------------------
  const handleOpenAddMechanism = () => {
    setMechForm({
      mechanismID: null,
      code: `MECH_${Date.now().toString().slice(-4)}`,
      name: '',
      category: 'Enzymatic (Beta-Lactamase)',
      organismScope: 'Enterobacterales, Pseudomonas aeruginosa, Acinetobacter baumannii',
      associatedPhenotype: 'Resistant to 3rd/4th Gen Cephalosporins / Carbapenems',
      infectionControlAlert: true,
      alertType: 'Critical Isolation Alert',
      clinicalSignificance: 'Requires contact precautions, enhanced environmental cleaning and stewardship review.',
      status: 'Active',
      isActive: true
    });
    setMechErrors({});
    setIsMechModalOpen(true);
  };

  const handleEditMechanism = (m) => {
    setMechForm({
      mechanismID: m.mechanismID,
      code: m.code || '',
      name: m.name || '',
      category: m.category || 'Enzymatic Resistance',
      organismScope: m.organismScope || 'Gram-Negative Bacilli',
      associatedPhenotype: m.associatedPhenotype || 'Extended-Spectrum Beta-Lactamase',
      infectionControlAlert: m.infectionControlAlert !== false,
      alertType: m.infectionControlAlert ? 'Critical Isolation Alert' : 'Standard Record',
      clinicalSignificance: m.clinicalSignificance || '',
      status: m.isActive !== false ? 'Active' : 'Inactive',
      isActive: m.isActive !== false
    });
    setMechErrors({});
    setIsMechModalOpen(true);
  };

  const handleSaveMechanism = async () => {
    const errors = {};
    if (!mechForm.code.trim()) errors.code = 'Mechanism Code is required';
    if (!mechForm.name.trim()) errors.name = 'Mechanism Name is required';

    if (Object.keys(errors).length > 0) {
      setMechErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        mechanismID: mechForm.mechanismID || 0,
        code: mechForm.code.trim().toUpperCase(),
        name: mechForm.name.trim(),
        clinicalSignificance: mechForm.clinicalSignificance || '',
        infectionControlAlert: !!mechForm.infectionControlAlert,
        displayOrder: mechForm.displayOrder || 0,
        isActive: mechForm.status === 'Active'
      };

      await microbiologyService.saveResistanceMechanism(payload);
      setIsMechModalOpen(false);
      showToast(`Resistance Mechanism "${payload.name}" saved successfully!`, 'success');
      loadData();
    } catch (err) {
      console.error('Error saving resistance mechanism:', err);
      showToast(err.response?.data?.message || 'Error saving resistance mechanism.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Tab 3: Comment Templates Handlers
  // -------------------------------------------------------------
  const handleOpenCreateComment = () => {
    setCommentForm({
      commentID: null,
      category: 'AST',
      title: '',
      commentText: '',
      usageScope: 'Automated susceptibility report footer',
      status: 'Active',
      isActive: true
    });
    setCommentErrors({});
    setIsCommentModalOpen(true);
  };

  const handleEditComment = (c) => {
    setCommentForm({
      commentID: c.commentID,
      category: c.category || 'AST',
      title: c.title || '',
      commentText: c.commentText || '',
      usageScope: c.usageScope || 'Routine reporting',
      status: c.isActive !== false ? 'Active' : 'Inactive',
      isActive: c.isActive !== false
    });
    setCommentErrors({});
    setIsCommentModalOpen(true);
  };

  const insertVariableIntoComment = (variableName) => {
    setCommentForm((prev) => ({
      ...prev,
      commentText: prev.commentText ? `${prev.commentText} {${variableName}}` : `{${variableName}}`
    }));
  };

  const handleSaveComment = async () => {
    const errors = {};
    if (!commentForm.title.trim()) errors.title = 'Template Title is required';
    if (!commentForm.commentText.trim()) errors.commentText = 'Comment Body is required';

    if (Object.keys(errors).length > 0) {
      setCommentErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        commentID: commentForm.commentID || 0,
        category: commentForm.category || 'General',
        title: commentForm.title.trim(),
        commentText: commentForm.commentText.trim(),
        specimenID: commentForm.specimenID ? parseInt(commentForm.specimenID) : null,
        organismID: commentForm.organismID ? parseInt(commentForm.organismID) : null,
        displayOrder: commentForm.displayOrder || 0,
        isActive: commentForm.status === 'Active'
      };

      await microbiologyService.saveCommentTemplate(payload);
      setIsCommentModalOpen(false);
      showToast(`Comment Template "${payload.title}" saved successfully!`, 'success');
      loadData();
    } catch (err) {
      console.error('Error saving comment template:', err);
      showToast(err.response?.data?.message || 'Error saving comment template.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Filtered lists
  // -------------------------------------------------------------
  const filteredRules = useMemo(() => {
    return rules.filter((r) => {
      const q = ruleSearch.toLowerCase();
      const matchesSearch = !ruleSearch ||
        r.ruleCode?.toLowerCase().includes(q) ||
        r.ruleName?.toLowerCase().includes(q) ||
        r.actionType?.toLowerCase().includes(q);

      const matchesStatus = ruleStatusFilter === 'ALL' ||
        (ruleStatusFilter === 'Active' && r.isActive) ||
        (ruleStatusFilter === 'Inactive' && !r.isActive);

      const matchesAction = ruleActionFilter === 'ALL' || r.actionType === ruleActionFilter;

      return matchesSearch && matchesStatus && matchesAction;
    });
  }, [rules, ruleSearch, ruleStatusFilter, ruleActionFilter]);

  const filteredMechanisms = useMemo(() => {
    return mechanisms.filter((m) => {
      const q = mechSearch.toLowerCase();
      const matchesSearch = !mechSearch ||
        m.code?.toLowerCase().includes(q) ||
        m.name?.toLowerCase().includes(q) ||
        m.clinicalSignificance?.toLowerCase().includes(q);

      const matchesStatus = mechStatusFilter === 'ALL' ||
        (mechStatusFilter === 'Active' && m.isActive !== false) ||
        (mechStatusFilter === 'Inactive' && m.isActive === false);

      const matchesAlert = mechAlertFilter === 'ALL' ||
        (mechAlertFilter === 'ALERT' && m.infectionControlAlert) ||
        (mechAlertFilter === 'STANDARD' && !m.infectionControlAlert);

      return matchesSearch && matchesStatus && matchesAlert;
    });
  }, [mechanisms, mechSearch, mechStatusFilter, mechAlertFilter]);

  const filteredComments = useMemo(() => {
    return comments.filter((c) => {
      const q = commentSearch.toLowerCase();
      const matchesSearch = !commentSearch ||
        c.title?.toLowerCase().includes(q) ||
        c.commentText?.toLowerCase().includes(q) ||
        c.category?.toLowerCase().includes(q);

      const matchesCategory = commentCategoryFilter === 'ALL' || c.category === commentCategoryFilter;

      const matchesStatus = commentStatusFilter === 'ALL' ||
        (commentStatusFilter === 'Active' && c.isActive !== false) ||
        (commentStatusFilter === 'Inactive' && c.isActive === false);

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [comments, commentSearch, commentCategoryFilter, commentStatusFilter]);

  // Standard Pagination Hooks for all 3 Master sub-catalogs
  const rulesPagination = usePagination({
    items: filteredRules,
    defaultPageSize: 10,
    resetDeps: [ruleSearch, ruleActionFilter, ruleStatusFilter, activeTab]
  });

  const mechPagination = usePagination({
    items: filteredMechanisms,
    defaultPageSize: 10,
    resetDeps: [mechSearch, mechAlertFilter, mechStatusFilter, activeTab]
  });

  const commentPagination = usePagination({
    items: filteredComments,
    defaultPageSize: 10,
    resetDeps: [commentSearch, commentCategoryFilter, commentStatusFilter, activeTab]
  });

  // Dynamic Header Action Button click
  const handlePrimaryCreateClick = () => {
    if (activeTab === 'rules') handleOpenCreateRule();
    else if (activeTab === 'mechanisms') handleOpenAddMechanism();
    else handleOpenCreateComment();
  };

  const getPrimaryButtonLabel = () => {
    if (activeTab === 'rules') return '+ Create Expert Rule';
    if (activeTab === 'mechanisms') return '+ Add Resistance Mechanism';
    return '+ Create Comment Template';
  };

  // Helper for human-readable rule condition string
  const formatRuleConditionPreview = (rule) => {
    try {
      const c = JSON.parse(rule.conditionJSON || '{}');
      const parts = [];
      if (rule.groupName || c.organismGroup) parts.push(`Group: ${rule.groupName || c.organismGroup}`);
      if (rule.organismName || c.organismCode) parts.push(`Org: ${rule.organismName || c.organismCode}`);
      if (c.drugCode) parts.push(`Drug: ${c.drugCode}`);
      if (c.interpretation) parts.push(`AST: ${c.interpretation}`);
      if (c.zoneThreshold) parts.push(`Zone: ${c.zoneThreshold}`);
      return parts.length > 0 ? parts.join(' | ') : 'Global rule matching';
    } catch (e) {
      return rule.conditionJSON || 'Rule condition configured';
    }
  };

  // Helper for human-readable action string
  const formatRuleActionPreview = (rule) => {
    try {
      const a = JSON.parse(rule.actionPayloadJSON || '{}');
      if (rule.actionType === 'OVERRIDE') return `Override Result → ${a.override || 'R'}`;
      if (rule.actionType === 'CRITICAL') return `Critical Alert: ${a.title || 'Infection Alert'}`;
      if (rule.actionType === 'SUPPRESS') return `Suppress: ${a.suppressDrugs ? a.suppressDrugs.join(', ') : 'Selective'}`;
      if (rule.actionType === 'COMMENT') return `Append Note: "${(a.comment || '').slice(0, 32)}..."`;
      return a.warning || a.message || rule.actionType;
    } catch (e) {
      return rule.actionType;
    }
  };

  return (
    <div className="micro-master-container">
      {/* Toast Notification Alert */}
      {toast.show && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '24px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
            background: toast.type === 'error' ? '#ef4444' : '#10b981',
            color: '#ffffff',
            fontWeight: '600',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span>{toast.type === 'error' ? '⚠️' : '✓'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="micro-header-section">
        <div className="micro-header-title">
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Clinical Expert Rules, Resistance & Comments Studio
          </h1>
          <p>Configure validated microbiology rules, resistance mechanisms, clinical alerts and standardized reporting comments.</p>
        </div>

        {/* Dynamic Primary Action Button */}
        <div className="micro-header-actions">
          <button
            type="button"
            className="micro-btn-primary"
            onClick={handlePrimaryCreateClick}
            id="btn-expert-studio-primary-action"
          >
            {getPrimaryButtonLabel()}
          </button>
        </div>
      </div>

      {/* Dynamic Summary Metric Badges */}
      <div className="micro-summary-bar" style={{ marginBottom: '18px' }}>
        <div className="micro-summary-item">
          <strong>Expert Rules:</strong> <span className="micro-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>{rules.length} Configured</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <strong>Resistance Mechanisms:</strong> <span className="micro-badge" style={{ background: '#fef3c7', color: '#92400e' }}>{mechanisms.length} Tracked</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <strong>Comment Templates:</strong> <span className="micro-badge" style={{ background: '#ede9fe', color: '#6d28d9' }}>{comments.length} Standardized</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <span style={{ fontSize: '0.78rem', color: '#166534' }}>
            ● System Status: Engine Active & Validating AST Outcomes
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="micro-studio-tabs">
        <button
          type="button"
          className={`micro-studio-tab-btn ${activeTab === 'rules' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          <span>🛡️ Expert Validation Rules</span>
          <span className="micro-badge" style={{ background: activeTab === 'rules' ? '#0284c7' : '#e2e8f0', color: activeTab === 'rules' ? '#fff' : '#475569' }}>
            {rules.length}
          </span>
        </button>

        <button
          type="button"
          className={`micro-studio-tab-btn ${activeTab === 'mechanisms' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('mechanisms')}
        >
          <span>🧬 Resistance Mechanisms</span>
          <span className="micro-badge" style={{ background: activeTab === 'mechanisms' ? '#0284c7' : '#e2e8f0', color: activeTab === 'mechanisms' ? '#fff' : '#475569' }}>
            {mechanisms.length}
          </span>
        </button>

        <button
          type="button"
          className={`micro-studio-tab-btn ${activeTab === 'comments' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          <span>💬 Comment Template Library</span>
          <span className="micro-badge" style={{ background: activeTab === 'comments' ? '#0284c7' : '#e2e8f0', color: activeTab === 'comments' ? '#fff' : '#475569' }}>
            {comments.length}
          </span>
        </button>
      </div>

      {/* ========================================================= */}
      {/* TAB 1: EXPERT VALIDATION RULES                            */}
      {/* ========================================================= */}
      {activeTab === 'rules' && (
        <div>
          {/* Filter / Search Bar */}
          <div className="micro-filter-bar">
            <div className="micro-search-input">
              <input
                type="text"
                placeholder="Search rules by code, name, drug, organism..."
                value={ruleSearch}
                onChange={(e) => setRuleSearch(e.target.value)}
              />
            </div>

            <select
              className="micro-filter-select"
              value={ruleActionFilter}
              onChange={(e) => setRuleActionFilter(e.target.value)}
            >
              <option value="ALL">All Action Types</option>
              <option value="OVERRIDE">OVERRIDE (Result Replacement)</option>
              <option value="CRITICAL">CRITICAL (Infection Alert)</option>
              <option value="SUPPRESS">SUPPRESS (Selective Masking)</option>
              <option value="COMMENT">COMMENT (Clinical Note)</option>
              <option value="WARN">WARN (Phenotype Warning)</option>
            </select>

            <select
              className="micro-filter-select"
              value={ruleStatusFilter}
              onChange={(e) => setRuleStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="Active">Active Only</option>
              <option value="Inactive">Inactive / Draft</option>
            </select>
          </div>

          {/* Rules Data Table */}
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Priority</th>
                  <th style={{ width: '130px' }}>Rule Code</th>
                  <th>Rule Name</th>
                  <th>Clinical Condition Scope</th>
                  <th>Action Triggered</th>
                  <th style={{ width: '90px' }}>Status</th>
                  <th style={{ width: '160px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      Loading expert validation rules...
                    </td>
                  </tr>
                ) : filteredRules.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🛡️</div>
                      <div style={{ fontWeight: '600', color: '#334155' }}>No Expert Rules Configured</div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '4px 0 12px 0' }}>
                        Define intrinsic resistance algorithms, atypical phenotype rules, and automated clinical alerts.
                      </div>
                      <button type="button" className="micro-btn-primary" onClick={handleOpenCreateRule}>
                        + Create First Expert Rule
                      </button>
                    </td>
                  </tr>
                ) : (
                  rulesPagination.pagedItems.map((rule) => (
                    <tr key={rule.ruleID}>
                      <td>
                        <strong style={{ color: '#0369a1' }}>#{rule.priority}</strong>
                      </td>
                      <td>
                        <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                          {rule.ruleCode}
                        </strong>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600', color: '#0f172a' }}>{rule.ruleName}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.82rem', color: '#334155', background: '#f8fafc', padding: '3px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'inline-block' }}>
                          {formatRuleConditionPreview(rule)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span
                            className="micro-badge"
                            style={{
                              background:
                                rule.actionType === 'CRITICAL'
                                  ? '#fee2e2'
                                  : rule.actionType === 'OVERRIDE'
                                  ? '#fef3c7'
                                  : rule.actionType === 'SUPPRESS'
                                  ? '#f1f5f9'
                                  : '#e0f2fe',
                              color:
                                rule.actionType === 'CRITICAL'
                                  ? '#b91c1c'
                                  : rule.actionType === 'OVERRIDE'
                                  ? '#b45309'
                                  : rule.actionType === 'SUPPRESS'
                                  ? '#475569'
                                  : '#0369a1'
                            }}
                          >
                            {rule.actionType}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#475569' }}>
                            {formatRuleActionPreview(rule)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`ast-status-badge ${rule.isActive ? 'status-active' : 'status-draft'}`}>
                          <span className="micro-status-dot" style={{ background: rule.isActive ? '#16a34a' : '#d97706' }} />
                          {rule.isActive ? 'Active' : 'Draft'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '4px' }}>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="View Rule Details & JSON"
                            onClick={() => setViewDetailModal({ isOpen: true, type: 'rule', data: rule })}
                          >
                            👁 View
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Edit Rule"
                            onClick={() => handleEditRule(rule)}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Duplicate Rule"
                            onClick={() => handleDuplicateRule(rule)}
                          >
                            📋 Copy
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Audit / Version History"
                            onClick={() => setHistoryModal({ isOpen: true, type: 'rule', data: rule })}
                          >
                            📜 History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <CommonPagination {...rulesPagination.paginationProps} />
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 2: RESISTANCE MECHANISMS                              */}
      {/* ========================================================= */}
      {activeTab === 'mechanisms' && (
        <div>
          {/* Filter / Search Bar */}
          <div className="micro-filter-bar">
            <div className="micro-search-input">
              <input
                type="text"
                placeholder="Search mechanisms by code, name, clinical significance..."
                value={mechSearch}
                onChange={(e) => setMechSearch(e.target.value)}
              />
            </div>

            <select
              className="micro-filter-select"
              value={mechAlertFilter}
              onChange={(e) => setMechAlertFilter(e.target.value)}
            >
              <option value="ALL">All Alert Types</option>
              <option value="ALERT">Infection Control Alerts Only</option>
              <option value="STANDARD">Standard Records Only</option>
            </select>

            <select
              className="micro-filter-select"
              value={mechStatusFilter}
              onChange={(e) => setMechStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="Active">Active Only</option>
              <option value="Inactive">Inactive Only</option>
            </select>
          </div>

          {/* Mechanisms Data Table */}
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Code</th>
                  <th>Resistance Mechanism Name</th>
                  <th>Infection Control Alert Policy</th>
                  <th>Clinical Significance & Epidemiology</th>
                  <th style={{ width: '90px' }}>Status</th>
                  <th style={{ width: '180px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMechanisms.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🧬</div>
                      <div style={{ fontWeight: '600', color: '#334155' }}>No Resistance Mechanisms Configured</div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '4px 0 12px 0' }}>
                        Track ESBL, Carbapenemases, MRSA, VRE and critical antimicrobial resistance mechanisms.
                      </div>
                      <button type="button" className="micro-btn-primary" onClick={handleOpenAddMechanism}>
                        + Add First Resistance Mechanism
                      </button>
                    </td>
                  </tr>
                ) : (
                  mechPagination.pagedItems.map((m) => (
                    <tr key={m.mechanismID}>
                      <td>
                        <strong style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', color: '#0369a1' }}>
                          {m.code}
                        </strong>
                      </td>
                      <td>
                        <strong style={{ color: '#0f172a' }}>{m.name}</strong>
                      </td>
                      <td>
                        {m.infectionControlAlert ? (
                          <span className="micro-badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                            ⚠️ Mandatory Isolation Alert
                          </span>
                        ) : (
                          <span className="micro-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>
                            Standard Clinical Record
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.82rem', color: '#475569' }}>
                        {m.clinicalSignificance || 'Clinical antimicrobial resistance mechanism'}
                      </td>
                      <td>
                        <span className={`ast-status-badge ${m.isActive !== false ? 'status-active' : 'status-inactive'}`}>
                          <span className="micro-status-dot" style={{ background: m.isActive !== false ? '#16a34a' : '#64748b' }} />
                          {m.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '4px' }}>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="View Details"
                            onClick={() => setViewDetailModal({ isOpen: true, type: 'mechanism', data: m })}
                          >
                            👁 View
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Edit Mechanism"
                            onClick={() => handleEditMechanism(m)}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Audit / History"
                            onClick={() => setHistoryModal({ isOpen: true, type: 'mechanism', data: m })}
                          >
                            📜 History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <CommonPagination {...mechPagination.paginationProps} />
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 3: COMMENT TEMPLATE LIBRARY                           */}
      {/* ========================================================= */}
      {activeTab === 'comments' && (
        <div>
          {/* Filter / Search Bar */}
          <div className="micro-filter-bar">
            <div className="micro-search-input">
              <input
                type="text"
                placeholder="Search templates by code, title, standard comment body..."
                value={commentSearch}
                onChange={(e) => setCommentSearch(e.target.value)}
              />
            </div>

            <select
              className="micro-filter-select"
              value={commentCategoryFilter}
              onChange={(e) => setCommentCategoryFilter(e.target.value)}
            >
              <option value="ALL">All Categories</option>
              <option value="AST">AST (Susceptibility)</option>
              <option value="Culture">Culture (Organism & Colony)</option>
              <option value="Microscopy">Microscopy & Gram Stain</option>
              <option value="Reporting">Reporting & Disclaimer</option>
              <option value="Quality">Quality / Specimen Advisory</option>
            </select>

            <select
              className="micro-filter-select"
              value={commentStatusFilter}
              onChange={(e) => setCommentStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="Active">Active Only</option>
              <option value="Inactive">Inactive Only</option>
            </select>
          </div>

          {/* Comments Data Table */}
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>Category</th>
                  <th style={{ width: '240px' }}>Title</th>
                  <th>Standard Clinical Comment Body</th>
                  <th style={{ width: '90px' }}>Status</th>
                  <th style={{ width: '180px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredComments.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>💬</div>
                      <div style={{ fontWeight: '600', color: '#334155' }}>No Comment Templates Configured</div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '4px 0 12px 0' }}>
                        Build standardized reporting comments, clinical disclaimers, and automated footers.
                      </div>
                      <button type="button" className="micro-btn-primary" onClick={handleOpenCreateComment}>
                        + Create First Comment Template
                      </button>
                    </td>
                  </tr>
                ) : (
                  commentPagination.pagedItems.map((c) => (
                    <tr key={c.commentID}>
                      <td>
                        <span className="micro-badge" style={{ background: '#ede9fe', color: '#6d28d9' }}>
                          {c.category}
                        </span>
                      </td>
                      <td>
                        <strong style={{ color: '#0f172a' }}>{c.title}</strong>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.85rem', color: '#334155', lineHeight: '1.4' }}>
                          {c.commentText}
                        </div>
                      </td>
                      <td>
                        <span className={`ast-status-badge ${c.isActive !== false ? 'status-active' : 'status-inactive'}`}>
                          <span className="micro-status-dot" style={{ background: c.isActive !== false ? '#16a34a' : '#64748b' }} />
                          {c.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '4px' }}>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="View Preview"
                            onClick={() => setViewDetailModal({ isOpen: true, type: 'comment', data: c })}
                          >
                            👁 View
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Edit Comment"
                            onClick={() => handleEditComment(c)}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="ast-action-icon-btn"
                            title="Audit / History"
                            onClick={() => setHistoryModal({ isOpen: true, type: 'comment', data: c })}
                          >
                            📜 History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <CommonPagination {...commentPagination.paginationProps} />
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 1: CREATE / EDIT EXPERT VALIDATION RULE             */}
      {/* ========================================================= */}
      {isRuleModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '880px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  {ruleForm.ruleID ? 'Edit Expert Validation Rule' : 'Create Expert Validation Rule'}
                </h3>
                <p>Configure validated microbiology rules, intrinsic resistance algorithms and automated clinical alerts.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsRuleModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="micro-modal-body">
              {/* Section 1: Rule Identity */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>1. Rule Identity & Priority</span>
                  <span className="section-badge">Standard Definition</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>
                      Rule Code <span className="req-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. RULE_MRSA_OXACILLIN"
                      value={ruleForm.ruleCode}
                      className={ruleErrors.ruleCode ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setRuleForm({ ...ruleForm, ruleCode: e.target.value.toUpperCase() });
                        setRuleErrors({ ...ruleErrors, ruleCode: null });
                      }}
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontWeight: '600' }}
                    />
                    {ruleErrors.ruleCode && <span className="micro-form-error">{ruleErrors.ruleCode}</span>}
                  </div>

                  <div className="micro-form-group" style={{ gridColumn: 'span 2' }}>
                    <label>
                      Rule Name <span className="req-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Staph aureus Cefoxitin Screen to Oxacillin/Beta-lactam Override"
                      value={ruleForm.ruleName}
                      className={ruleErrors.ruleName ? 'is-invalid' : ''}
                      onChange={(e) => {
                        setRuleForm({ ...ruleForm, ruleName: e.target.value });
                        setRuleErrors({ ...ruleErrors, ruleName: null });
                      }}
                    />
                    {ruleErrors.ruleName && <span className="micro-form-error">{ruleErrors.ruleName}</span>}
                  </div>
                </div>

                <div className="micro-form-grid-3" style={{ marginTop: '12px' }}>
                  <div className="micro-form-group">
                    <label>Execution Priority <span className="req-star">*</span></label>
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={ruleForm.priority}
                      onChange={(e) => setRuleForm({ ...ruleForm, priority: e.target.value })}
                    />
                    <span className="micro-form-helper">Lower number executes earlier in pipeline.</span>
                  </div>

                  <div className="micro-form-group" style={{ gridColumn: 'span 2' }}>
                    <label>Status <span className="req-star">*</span></label>
                    <div className="micro-status-group">
                      {['Draft', 'Under Review', 'Approved', 'Active', 'Inactive'].map((st) => (
                        <button
                          key={st}
                          type="button"
                          className={`micro-status-btn ${
                            ruleForm.status === st ? `active-status-${st.toLowerCase().replace(' ', '-')}` : ''
                          }`}
                          onClick={() => setRuleForm({ ...ruleForm, status: st, isActive: st === 'Active' || st === 'Approved' })}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Clinical Scope */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>2. Clinical Scope</span>
                  <span className="section-badge">Master Entities</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>Applicable Organism Group</label>
                    <select
                      value={ruleForm.organismGroupID}
                      onChange={(e) => setRuleForm({ ...ruleForm, organismGroupID: e.target.value })}
                    >
                      <option value="">-- Applicable to ALL Organism Groups --</option>
                      {groups.map((g) => (
                        <option key={g.organismGroupID} value={g.organismGroupID}>
                          {g.groupName} ({g.gramType})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Specific Organism (Optional Override)</label>
                    <select
                      value={ruleForm.organismID}
                      onChange={(e) => setRuleForm({ ...ruleForm, organismID: e.target.value })}
                    >
                      <option value="">-- No Specific Organism Override --</option>
                      {organisms.map((o) => (
                        <option key={o.organismID} value={o.organismID}>
                          {o.organismName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Specimen Matrix</label>
                    <select
                      value={ruleForm.specimenID}
                      onChange={(e) => setRuleForm({ ...ruleForm, specimenID: e.target.value })}
                    >
                      <option value="">-- All Specimen Matrices --</option>
                      {specimens.map((sp) => (
                        <option key={sp.specimenID} value={sp.specimenID}>
                          {sp.specimenName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 3: Visual Condition Builder */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>3. Visual Condition Builder</span>
                  <button
                    type="button"
                    className="ast-action-icon-btn"
                    style={{ marginLeft: 'auto', textTransform: 'none' }}
                    onClick={() => setRuleForm({ ...ruleForm, showRawConditionJSON: !ruleForm.showRawConditionJSON })}
                  >
                    {ruleForm.showRawConditionJSON ? 'Show Visual Builder' : 'Advanced JSON View'}
                  </button>
                </div>

                {!ruleForm.showRawConditionJSON ? (
                  <div className="micro-condition-block">
                    <div className="micro-condition-row">
                      <span className="micro-logic-tag micro-logic-when">WHEN</span>
                      <span style={{ fontSize: '0.84rem', fontWeight: '600', color: '#334155' }}>Target Antimicrobial:</span>
                      <select
                        style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                        value={ruleForm.targetDrugCode}
                        onChange={(e) => setRuleForm({ ...ruleForm, targetDrugCode: e.target.value })}
                      >
                        {antimicrobials.map((a) => (
                          <option key={a.antimicrobialID} value={a.drugCode}>
                            {a.drugName} ({a.drugCode})
                          </option>
                        ))}
                      </select>

                      <span className="micro-logic-tag micro-logic-and">AND</span>
                      <span style={{ fontSize: '0.84rem', fontWeight: '600', color: '#334155' }}>Observed Result:</span>
                      <select
                        style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                        value={ruleForm.expectedInterpretation}
                        onChange={(e) => setRuleForm({ ...ruleForm, expectedInterpretation: e.target.value })}
                      >
                        <option value="R">Resistant (R)</option>
                        <option value="S">Susceptible (S)</option>
                        <option value="I">Intermediate / SDD (I)</option>
                        <option value="NS">Non-Susceptible (NS)</option>
                      </select>
                    </div>

                    <div className="micro-condition-row">
                      <span className="micro-logic-tag micro-logic-and">OPTIONAL</span>
                      <span style={{ fontSize: '0.84rem', color: '#64748b' }}>Zone / MIC threshold check:</span>
                      <select
                        style={{ padding: '4px 6px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                        value={ruleForm.zoneThresholdOperator}
                        onChange={(e) => setRuleForm({ ...ruleForm, zoneThresholdOperator: e.target.value })}
                      >
                        <option value="<=">&le; Less than or equal</option>
                        <option value=">=">&ge; Greater than or equal</option>
                        <option value="<">&lt; Less than</option>
                        <option value=">">&gt; Greater than</option>
                      </select>
                      <input
                        type="text"
                        placeholder="e.g. 21"
                        style={{ width: '80px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                        value={ruleForm.zoneThresholdVal}
                        onChange={(e) => setRuleForm({ ...ruleForm, zoneThresholdVal: e.target.value })}
                      />
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>mm (Disk Diffusion)</span>
                    </div>
                  </div>
                ) : (
                  <div className="micro-form-group">
                    <label>Condition Schema JSON (Advanced)</label>
                    <textarea
                      rows={4}
                      style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' }}
                      value={ruleForm.customConditionJSON || buildConditionJSON()}
                      onChange={(e) => setRuleForm({ ...ruleForm, customConditionJSON: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Section 4: Action Builder */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>4. Action & Action Payload</span>
                  <span className="section-badge">Rule Trigger</span>
                </div>

                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>Action Type <span className="req-star">*</span></label>
                    <select
                      value={ruleForm.actionType}
                      onChange={(e) => setRuleForm({ ...ruleForm, actionType: e.target.value })}
                    >
                      <option value="OVERRIDE">OVERRIDE (Change Interpretation)</option>
                      <option value="CRITICAL">CRITICAL (Infection Control Alert)</option>
                      <option value="SUPPRESS">SUPPRESS (Selective Masking)</option>
                      <option value="COMMENT">COMMENT (Append Standard Comment)</option>
                      <option value="WARN">WARN (Flag for Microbiologist Review)</option>
                    </select>
                  </div>

                  {ruleForm.actionType === 'OVERRIDE' && (
                    <div className="micro-form-group">
                      <label>Override AST Interpretation To:</label>
                      <select
                        value={ruleForm.overrideInterpretation}
                        onChange={(e) => setRuleForm({ ...ruleForm, overrideInterpretation: e.target.value })}
                      >
                        <option value="R">Resistant (R)</option>
                        <option value="S">Susceptible (S)</option>
                        <option value="I">Intermediate (I)</option>
                        <option value="NS">Non-Susceptible (NS)</option>
                      </select>
                    </div>
                  )}

                  {ruleForm.actionType === 'CRITICAL' && (
                    <div className="micro-form-group">
                      <label>Alert Severity</label>
                      <select
                        value={ruleForm.alertSeverity}
                        onChange={(e) => setRuleForm({ ...ruleForm, alertSeverity: e.target.value })}
                      >
                        <option value="HIGH">HIGH (Urgent Laboratory Flag)</option>
                        <option value="CRITICAL">CRITICAL (Mandatory Isolation Alert)</option>
                        <option value="MODERATE">MODERATE (Review Before Release)</option>
                      </select>
                    </div>
                  )}
                </div>

                {ruleForm.actionType === 'CRITICAL' && (
                  <div className="micro-form-grid-2" style={{ marginTop: '10px' }}>
                    <div className="micro-form-group">
                      <label>Alert Title</label>
                      <input
                        type="text"
                        value={ruleForm.alertTitle}
                        onChange={(e) => setRuleForm({ ...ruleForm, alertTitle: e.target.value })}
                      />
                    </div>
                    <div className="micro-form-group">
                      <label>Alert Message</label>
                      <input
                        type="text"
                        value={ruleForm.alertMessage}
                        onChange={(e) => setRuleForm({ ...ruleForm, alertMessage: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {ruleForm.actionType === 'SUPPRESS' && (
                  <div className="micro-form-group" style={{ marginTop: '10px' }}>
                    <label>Antimicrobials to Suppress / Mask (Comma separated drug codes)</label>
                    <input
                      type="text"
                      placeholder="e.g. AMP, AMC, SAM, CRO"
                      value={ruleForm.suppressDrugCodes}
                      onChange={(e) => setRuleForm({ ...ruleForm, suppressDrugCodes: e.target.value })}
                    />
                    <span className="micro-form-helper">Masks reporting of these drugs on routine clinician reports.</span>
                  </div>
                )}

                {ruleForm.actionType === 'COMMENT' && (
                  <div className="micro-form-group" style={{ marginTop: '10px' }}>
                    <label>Standard Comment Body to Append</label>
                    <textarea
                      rows={2}
                      value={ruleForm.commentTemplateText}
                      onChange={(e) => setRuleForm({ ...ruleForm, commentTemplateText: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Section 5: Human-Readable Rule Preview */}
              <div className="micro-rule-preview-box">
                <div className="rule-title">Live Clinical Rule Preview</div>
                <div className="rule-content">
                  <div><strong>WHEN:</strong> Target Drug = <code>{ruleForm.targetDrugCode || 'Selected'}</code> is <code>{ruleForm.expectedInterpretation}</code> {ruleForm.zoneThresholdVal ? `(Zone ${ruleForm.zoneThresholdOperator} ${ruleForm.zoneThresholdVal}mm)` : ''}</div>
                  <div><strong>THEN:</strong> Action = <code>{ruleForm.actionType}</code> {ruleForm.actionType === 'OVERRIDE' ? `→ Force Interpretation to [${ruleForm.overrideInterpretation}]` : ''}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>
                    Priority: #{ruleForm.priority} &bull; Status: {ruleForm.status}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setIsRuleModalOpen(false)}
              >
                Cancel
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => handleSaveRule(false)}
                  disabled={saveLoading}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="micro-btn-primary"
                  onClick={() => handleSaveRule(true)}
                  disabled={saveLoading}
                >
                  {saveLoading ? 'Saving...' : 'Save & Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 2: ADD / EDIT RESISTANCE MECHANISM                  */}
      {/* ========================================================= */}
      {isMechModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '640px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {mechForm.mechanismID ? 'Edit Resistance Mechanism' : 'Add Resistance Mechanism'}
                </h3>
                <p>Define resistance phenotypes, epidemiological classifications and infection control alert policies.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsMechModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="micro-modal-body">
              <div className="micro-form-grid-2">
                <div className="micro-form-group">
                  <label>
                    Mechanism Code <span className="req-star">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. MECH_CRE"
                    value={mechForm.code}
                    className={mechErrors.code ? 'is-invalid' : ''}
                    onChange={(e) => {
                      setMechForm({ ...mechForm, code: e.target.value.toUpperCase() });
                      setMechErrors({ ...mechErrors, code: null });
                    }}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontWeight: '600' }}
                  />
                  {mechErrors.code && <span className="micro-form-error">{mechErrors.code}</span>}
                </div>

                <div className="micro-form-group">
                  <label>
                    Mechanism Name <span className="req-star">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Carbapenem-Resistant Enterobacterales (CRE)"
                    value={mechForm.name}
                    className={mechErrors.name ? 'is-invalid' : ''}
                    onChange={(e) => {
                      setMechForm({ ...mechForm, name: e.target.value });
                      setMechErrors({ ...mechErrors, name: null });
                    }}
                  />
                  {mechErrors.name && <span className="micro-form-error">{mechErrors.name}</span>}
                </div>
              </div>

              <div className="micro-form-group">
                <label>Category / Class</label>
                <select
                  value={mechForm.category}
                  onChange={(e) => setMechForm({ ...mechForm, category: e.target.value })}
                >
                  <option value="Enzymatic (Beta-Lactamase)">Enzymatic (Beta-Lactamase)</option>
                  <option value="Target Site Modification">Target Site Modification</option>
                  <option value="Efflux Pump / Permeability">Efflux Pump / Permeability</option>
                  <option value="Intrinsic Resistance">Intrinsic Resistance</option>
                  <option value="Acquired Multidrug Resistance">Acquired Multidrug Resistance</option>
                </select>
              </div>

              <div className="micro-form-group">
                <label>Infection Control Alert Policy</label>
                <select
                  value={mechForm.alertType}
                  onChange={(e) => {
                    const alertType = e.target.value;
                    setMechForm({
                      ...mechForm,
                      alertType,
                      infectionControlAlert: alertType !== 'No Alert'
                    });
                  }}
                >
                  <option value="Critical Isolation Alert">Critical Isolation Alert (Mandatory Contact Precautions)</option>
                  <option value="Infection Control Review">Infection Control Review Required</option>
                  <option value="Standard Alert">Standard Alert</option>
                  <option value="No Alert">No Alert (Internal Reference Only)</option>
                </select>
                <span className="micro-form-helper">Operational action follows institutional infection control policy.</span>
              </div>

              <div className="micro-form-group">
                <label>Clinical Significance</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Critical resistance mechanism requiring infection control notification."
                  value={mechForm.clinicalSignificance}
                  onChange={(e) => setMechForm({ ...mechForm, clinicalSignificance: e.target.value })}
                />
              </div>

              <div className="micro-form-group">
                <label>Status</label>
                <div className="micro-status-group">
                  {['Active', 'Draft', 'Inactive', 'Deprecated'].map((st) => (
                    <button
                      key={st}
                      type="button"
                      className={`micro-status-btn ${
                        mechForm.status === st ? `active-status-${st.toLowerCase()}` : ''
                      }`}
                      onClick={() => setMechForm({ ...mechForm, status: st, isActive: st === 'Active' })}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setIsMechModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                onClick={handleSaveMechanism}
              >
                Save Mechanism
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 3: CREATE / EDIT COMMENT TEMPLATE                   */}
      {/* ========================================================= */}
      {isCommentModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '680px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  {commentForm.commentID ? 'Edit Comment Template' : 'Create Comment Template'}
                </h3>
                <p>Standardize clinical comments, reporting footnotes and automated organism advisories.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsCommentModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="micro-modal-body">
              <div className="micro-form-grid-2">
                <div className="micro-form-group">
                  <label>
                    Category <span className="req-star">*</span>
                  </label>
                  <select
                    value={commentForm.category}
                    onChange={(e) => setCommentForm({ ...commentForm, category: e.target.value })}
                  >
                    <option value="AST">AST (Susceptibility)</option>
                    <option value="Culture">Culture (Organism & Colony)</option>
                    <option value="Microscopy">Microscopy & Gram Stain</option>
                    <option value="Reporting">Reporting & Disclaimer</option>
                    <option value="Quality">Quality / Specimen Advisory</option>
                  </select>
                </div>

                <div className="micro-form-group">
                  <label>
                    Template Title <span className="req-star">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. ESBL Clinical Warning Footer"
                    value={commentForm.title}
                    className={commentErrors.title ? 'is-invalid' : ''}
                    onChange={(e) => {
                      setCommentForm({ ...commentForm, title: e.target.value });
                      setCommentErrors({ ...commentErrors, title: null });
                    }}
                  />
                  {commentErrors.title && <span className="micro-form-error">{commentErrors.title}</span>}
                </div>
              </div>

              {/* Supported Variable Placeholder Chips */}
              <div className="micro-form-group">
                <label>Supported Report Variables (Click to insert):</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['ORGANISM', 'SPECIMEN', 'ANTIMICROBIAL', 'INTERPRETATION', 'VALUE'].map((v) => (
                    <span
                      key={v}
                      className="micro-variable-pill"
                      onClick={() => insertVariableIntoComment(v)}
                      title={`Insert {${v}} placeholder`}
                    >
                      +{'{'}{v}{'}'}
                    </span>
                  ))}
                </div>
              </div>

              <div className="micro-form-group">
                <label>
                  Standard Clinical Comment Body <span className="req-star">*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="e.g. This isolate produces Extended-Spectrum Beta-Lactamase (ESBL). All Penicillins, Cephalosporins and Aztreonam should be reported as Resistant regardless of in vitro testing results."
                  value={commentForm.commentText}
                  className={commentErrors.commentText ? 'is-invalid' : ''}
                  onChange={(e) => {
                    setCommentForm({ ...commentForm, commentText: e.target.value });
                    setCommentErrors({ ...commentErrors, commentText: null });
                  }}
                />
                {commentErrors.commentText && <span className="micro-form-error">{commentErrors.commentText}</span>}
              </div>

              {/* Live Preview Box */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#334155', marginBottom: '4px', display: 'block' }}>
                  Live Report Comment Preview:
                </label>
                <div className="micro-comment-preview">
                  {commentForm.commentText ? (
                    commentForm.commentText
                      .replace(/{ORGANISM}/g, 'Klebsiella pneumoniae')
                      .replace(/{SPECIMEN}/g, 'Blood Culture')
                      .replace(/{ANTIMICROBIAL}/g, 'Ceftriaxone')
                      .replace(/{INTERPRETATION}/g, 'Resistant')
                      .replace(/{VALUE}/g, '6 mm')
                  ) : (
                    <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                      Comment preview will appear here as you type...
                    </span>
                  )}
                </div>
              </div>

              <div className="micro-form-group">
                <label>Status</label>
                <div className="micro-status-group">
                  {['Active', 'Draft', 'Inactive'].map((st) => (
                    <button
                      key={st}
                      type="button"
                      className={`micro-status-btn ${
                        commentForm.status === st ? `active-status-${st.toLowerCase()}` : ''
                      }`}
                      onClick={() => setCommentForm({ ...commentForm, status: st, isActive: st === 'Active' })}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setIsCommentModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                onClick={handleSaveComment}
              >
                Save Comment Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 4: VIEW DETAIL MODAL                                */}
      {/* ========================================================= */}
      {viewDetailModal.isOpen && viewDetailModal.data && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '640px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  Record Details: {viewDetailModal.data.ruleCode || viewDetailModal.data.code || viewDetailModal.data.title}
                </h3>
                <p>Clinical overview, scope and raw configuration representation.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setViewDetailModal({ isOpen: false, type: null, data: null })}
              >
                &times;
              </button>
            </div>
            <div className="micro-modal-body">
              {viewDetailModal.type === 'rule' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="micro-section-card">
                    <div className="micro-section-title">Clinical Rule Overview</div>
                    <div style={{ fontSize: '0.88rem', color: '#1e293b', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div><strong>Rule Name:</strong> {viewDetailModal.data.ruleName}</div>
                      <div><strong>Priority:</strong> #{viewDetailModal.data.priority}</div>
                      <div><strong>Action Type:</strong> <span className="micro-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>{viewDetailModal.data.actionType}</span></div>
                      <div><strong>Status:</strong> {viewDetailModal.data.isActive ? 'Active' : 'Inactive'}</div>
                    </div>
                  </div>

                  <div className="micro-section-card">
                    <div className="micro-section-title">Condition Schema JSON</div>
                    <pre style={{ margin: 0, padding: '10px', background: '#f8fafc', borderRadius: '6px', fontSize: '0.8rem', overflowX: 'auto', border: '1px solid #e2e8f0' }}>
                      {viewDetailModal.data.conditionJSON}
                    </pre>
                  </div>

                  <div className="micro-section-card">
                    <div className="micro-section-title">Action Payload JSON</div>
                    <pre style={{ margin: 0, padding: '10px', background: '#f8fafc', borderRadius: '6px', fontSize: '0.8rem', overflowX: 'auto', border: '1px solid #e2e8f0' }}>
                      {viewDetailModal.data.actionPayloadJSON}
                    </pre>
                  </div>
                </div>
              )}

              {viewDetailModal.type === 'mechanism' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="micro-section-card">
                    <div className="micro-section-title">Resistance Mechanism Overview</div>
                    <div style={{ fontSize: '0.88rem', color: '#1e293b', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div><strong>Name:</strong> {viewDetailModal.data.name}</div>
                      <div><strong>Code:</strong> <code>{viewDetailModal.data.code}</code></div>
                      <div><strong>Alert Policy:</strong> {viewDetailModal.data.infectionControlAlert ? '⚠️ Mandatory Isolation Alert' : 'Standard'}</div>
                      <div><strong>Clinical Significance:</strong> {viewDetailModal.data.clinicalSignificance}</div>
                    </div>
                  </div>
                </div>
              )}

              {viewDetailModal.type === 'comment' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="micro-section-card">
                    <div className="micro-section-title">Comment Template Preview</div>
                    <div><strong>Category:</strong> {viewDetailModal.data.category}</div>
                    <div style={{ marginTop: '8px' }}><strong>Standard Text:</strong></div>
                    <div className="micro-comment-preview" style={{ marginTop: '6px' }}>
                      {viewDetailModal.data.commentText}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-primary"
                onClick={() => setViewDetailModal({ isOpen: false, type: null, data: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 5: AUDIT & HISTORY MODAL                            */}
      {/* ========================================================= */}
      {historyModal.isOpen && historyModal.data && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '600px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  📜 Audit & Version History
                </h3>
                <p>{historyModal.data.ruleCode || historyModal.data.code || historyModal.data.title}</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setHistoryModal({ isOpen: false, type: null, data: null })}
              >
                &times;
              </button>
            </div>
            <div className="micro-modal-body">
              <div className="micro-section-card">
                <div className="micro-section-title">Record Lifecycle & Traceability</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem' }}>
                  <div><strong>Current Version:</strong> v1.0 (Active Production Baseline)</div>
                  <div><strong>Created Date:</strong> System Initial Master Configuration</div>
                  <div><strong>Created By:</strong> Lead Clinical Microbiologist / System Admin</div>
                  <div><strong>Last Verification:</strong> Verified against CLSI/EUCAST Guidelines</div>
                  <div><strong>Approval Status:</strong> <span className="micro-badge" style={{ background: '#dcfce7', color: '#15803d' }}>Approved for Routine Validation</span></div>
                  <div><strong>Change Reason:</strong> Initial standardized institution rule deployment.</div>
                </div>
              </div>
            </div>
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-primary"
                onClick={() => setHistoryModal({ isOpen: false, type: null, data: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

